import { useState } from "react";
import { BATHROOM_PRODUCTS } from "@/data/visualizer";
import { PRODUCTS } from "@/data/products";

const DISCIPLINES = ["Tile", "Stone"];

/**
 * Picker used for both Floor and Wall in the Bathroom dual-material mode.
 * A small Tile / Stone segment control at the top narrows the list from 12
 * down to 6 so users can find what they want without scrolling. Colour chips
 * for the selected product appear inline underneath its row.
 */
export default function BathroomPicker({
  label,
  testid,
  value,
  onChange,
  colourIdx = 0,
  setColourIdx,
}) {
  const testKey = label.toLowerCase();
  const selected = BATHROOM_PRODUCTS.find((p) => p.id === value);
  // Default the segment tab to the discipline of the currently-selected item
  // so switching Floor/Wall doesn't hide the active choice.
  const [tab, setTab] = useState(selected?.discipline || "Tile");
  const visible = BATHROOM_PRODUCTS.filter((p) => p.discipline === tab);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">{label}</p>
        <div className="flex gap-1" data-testid={`${testid}-tabs`}>
          {DISCIPLINES.map((d) => {
            const on = d === tab;
            return (
              <button
                key={d}
                data-testid={`${testid}-tab-${d.toLowerCase()}`}
                onClick={() => setTab(d)}
                className="px-3 py-1 text-[10px] tracking-[0.28em] uppercase font-body transition-colors"
                style={{
                  borderBottom: on ? "1px solid #C9A876" : "1px solid transparent",
                  color: on ? "#F5F4F0" : "rgba(214,209,202,0.5)",
                }}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1 max-h-[320px] overflow-y-auto" data-testid={testid}>
        {visible.map((p) => {
          const on = p.id === value;
          const full = PRODUCTS.find((x) => x.id === p.id);
          const colours = full?.colours || [];
          return (
            <div key={p.id}>
              <button
                data-testid={`viz-${testKey}-${p.id}`}
                onClick={() => onChange(p.id)}
                className="w-full text-left px-3 py-2.5 border flex items-center gap-3"
                style={{
                  borderColor: on ? "#C9A876" : "rgba(214,209,202,0.12)",
                  background: on ? "rgba(201,168,118,0.12)" : "transparent",
                }}
              >
                {colours[0]?.hex && (
                  <span
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ background: colours[0].hex, boxShadow: "inset 0 0 0 1px rgba(214,209,202,0.35)" }}
                  />
                )}
                <span className="font-serif-display text-[#F5F4F0] text-sm truncate">{p.name}</span>
              </button>
              {on && setColourIdx && colours.length > 1 && (
                <div
                  className="mt-2 mb-1 px-3 flex items-center gap-2 flex-wrap"
                  data-testid={`viz-${testKey}-colours-${p.id}`}
                >
                  {colours.map((c, i) => {
                    const active = i === colourIdx;
                    return (
                      <button
                        key={c.name}
                        data-testid={`viz-${testKey}-colour-${p.id}-${i}`}
                        onClick={() => setColourIdx(i)}
                        title={c.name}
                        aria-label={c.name}
                        className="relative w-6 h-6 rounded-full transition-transform duration-200 hover:scale-110"
                        style={{
                          background: c.hex,
                          boxShadow: active
                            ? "0 0 0 1.5px #121315, 0 0 0 3px #C9A876"
                            : "inset 0 0 0 1px rgba(214,209,202,0.35)",
                        }}
                      />
                    );
                  })}
                  <span className="text-[#D6D1CA]/60 text-[9px] tracking-[0.28em] uppercase font-body ml-1">
                    {colours[colourIdx]?.name}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
